import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Camera, Eye, Heart, ShoppingBag, Upload, UserRound, Users } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { BrandPeriod, useBrandApi } from '../../../common/hooks/use-brand-api.hook';
import { KpiCard, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { BarList, MiniStat, Thumb, TrendChart } from '../../../components/design-system/analytics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { BrandPage, Change, LoadFailed, PeriodPills, PrivacyFootnote, SuppressedNotice, useBrandMe } from '../../../modules/brand/components/BrandUi';
import { countryName, rowLabel, signed, trendPoints, vocab } from '../../../modules/brand/brand-format';

const kpiDelta = (m?: { value: number | null; previous: number | null } | null) =>
    m && m.value != null && m.previous != null ? { current: m.value, previous: m.previous } : undefined;

/**
 * The brand at a glance: how much its products are tried in shoppers' rooms
 * across every retailer, where, which products, what is rising, its share of
 * its category, and what shoppers look for that nobody sells.
 */
export function BrandOverviewPage() {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const api = useBrandApi();
    const { me } = useBrandMe();
    const [period, setPeriod] = useState<BrandPeriod>('30d');

    const { data, loading, error, reload } = useLoader(async () => {
        if (!me?.connected) return null;
        const [overview, benchmark, unmet] = await Promise.all([
            api.overview(period),
            api.benchmark(period).catch(() => null),
            api.unmet(period).catch(() => null),
        ]);
        return { overview, benchmark, unmet };
    }, [period, me?.connected]);

    const o = data?.overview;
    const k = o?.kpis;
    const vsPrevious = t('BRAND_PORTAL.COMMON.VS_PREVIOUS', { count: Number(period.replace('d', '')) });
    const headline = data?.benchmark?.headline;
    const teaser = data?.unmet?.groups?.[0];

    return (
        <BrandPage
            testId="brand-overview"
            crumb={t('BRAND_PORTAL.NAV.OVERVIEW')}
            title={me?.brand?.name ?? t('BRAND_PORTAL.OVERVIEW.TITLE')}
            description={o ? t('BRAND_PORTAL.OVERVIEW.SUBTITLE', { count: o.catalogue.retailers.total }) : undefined}
            actions={<PeriodPills value={period} onChange={setPeriod} />}
        >
            {error && <LoadFailed onRetry={reload} />}

            {o && (
                <Card className="py-4" data-testid="brand-catalogue">
                    <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        <MiniStat label={t('BRAND_PORTAL.CATALOGUE.PRODUCTS')} value={formatNumber(o.catalogue.products)} sub={t('BRAND_PORTAL.CATALOGUE.COLLECTIONS', { count: o.catalogue.collections })} />
                        <MiniStat label={t('BRAND_PORTAL.CATALOGUE.DIGITIZED')} value={formatNumber(o.catalogue.digitized)} sub={t('BRAND_PORTAL.CATALOGUE.DIGITIZED_SUB', { count: o.catalogue.products })} />
                        <MiniStat label={t('BRAND_PORTAL.CATALOGUE.RETAILERS')} value={formatNumber(o.catalogue.retailers.total)} sub={t('BRAND_PORTAL.CATALOGUE.RETAILERS_SUB', { listed: o.catalogue.retailers.listed, anonymous: o.catalogue.retailers.anonymous })} />
                        <MiniStat label={t('BRAND_PORTAL.CATALOGUE.SHARING')} value={formatNumber(o.catalogue.retailers.sharingDemand)} sub={t('BRAND_PORTAL.CATALOGUE.SHARING_SUB')} />
                    </CardContent>
                </Card>
            )}

            {o?.suppressed ? (
                <SuppressedNotice reason={o.suppressed.reason} />
            ) : (
                !error && (
                    <>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            <KpiCard testId="brand-kpi-visualizations" label={t('BRAND_PORTAL.KPI.VISUALIZATIONS')} value={formatNumber(k?.visualizations.value)} delta={kpiDelta(k?.visualizations)} hint={vsPrevious} icon={<Eye />} loading={loading} trend={o ? o.trend.map((p) => p.visualizations ?? 0) : undefined} href="/brand/demand" />
                            <KpiCard testId="brand-kpi-shoppers" label={t('BRAND_PORTAL.KPI.SHOPPERS')} value={formatNumber(k?.shoppers.value)} delta={kpiDelta(k?.shoppers)} hint={vsPrevious} icon={<Users />} loading={loading} />
                            <KpiCard testId="brand-kpi-uploads" label={t('BRAND_PORTAL.KPI.ROOM_UPLOADS')} value={formatNumber(k?.roomUploads.value)} delta={kpiDelta(k?.roomUploads)} hint={k?.roomUploads.value == null ? t('BRAND_PORTAL.PRIVACY.WITHHELD') : t('BRAND_PORTAL.KPI.ROOM_UPLOADS_HINT')} icon={<Upload />} loading={loading} />
                            <KpiCard testId="brand-kpi-favourites" label={t('BRAND_PORTAL.KPI.FAVOURITES')} value={formatNumber(k?.favourites.value)} delta={kpiDelta(k?.favourites)} hint={vsPrevious} icon={<Heart />} loading={loading} />
                            <KpiCard testId="brand-kpi-buy" label={t('BRAND_PORTAL.KPI.BUY_CLICKS')} value={formatNumber(k?.buyClicks.value)} delta={kpiDelta(k?.buyClicks)} hint={t('BRAND_PORTAL.KPI.BUY_CLICKS_HINT')} icon={<ShoppingBag />} loading={loading} />
                            <KpiCard testId="brand-kpi-leads" label={t('BRAND_PORTAL.KPI.LEADS')} value={formatNumber(k?.leads.value)} delta={kpiDelta(k?.leads)} hint={t('BRAND_PORTAL.KPI.LEADS_HINT')} icon={<UserRound />} loading={loading} />
                        </div>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                            <SectionCard className="min-w-0 lg:col-span-3" title={t('BRAND_PORTAL.OVERVIEW.TREND_TITLE')} description={t(o?.granularity === 'week' ? 'BRAND_PORTAL.OVERVIEW.TREND_WEEK' : 'BRAND_PORTAL.OVERVIEW.TREND_DAY')} testId="brand-trend">
                                {/* Charts keep left-to-right time in every language, as the rest of the app's do. */}
                                <div dir="ltr">
                                    <TrendChart data={o ? (trendPoints(o.trend, lang) as any) : []} valueLabel={t('BRAND_PORTAL.COMMON.THIS_PERIOD')} previousLabel={t('BRAND_PORTAL.COMMON.PREVIOUS_PERIOD')} height={240} partialLast partialLabel={t('VIZ_ANALYTICS.TREND.SO_FAR')} />
                                </div>
                                <div className="mt-3 flex flex-wrap gap-6">
                                    <MiniStat label={t('BRAND_PORTAL.KPI.RENDERS')} value={formatNumber(k?.renders.value)} />
                                    <MiniStat label={t('BRAND_PORTAL.KPI.DOWNLOADS')} value={formatNumber(k?.downloads.value)} />
                                </div>
                            </SectionCard>

                            <SectionCard className="min-w-0 lg:col-span-2" title={t('BRAND_PORTAL.BENCHMARK.TITLE')} description={t('BRAND_PORTAL.BENCHMARK.DESCRIPTION')} testId="brand-share">
                                {headline ? (
                                    <div className="flex flex-col gap-3">
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-heading text-4xl leading-none font-medium tracking-tight tabular-nums">{headline.share}%</span>
                                            {headline.changePts != null && Math.abs(headline.changePts) >= 0.1 && (
                                                <span className={headline.changePts > 0 ? 'text-sm font-medium text-success' : 'text-sm font-medium text-destructive'}>
                                                    {t('BRAND_PORTAL.BENCHMARK.PTS', { value: signed(headline.changePts, 1) })}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t('BRAND_PORTAL.BENCHMARK.HEADLINE', {
                                                look: vocab(t, headline.category).toLowerCase(),
                                                material: data?.benchmark?.brandMaterial ? vocab(t, data.benchmark.brandMaterial).toLowerCase() : t('BRAND_PORTAL.BENCHMARK.PRODUCTS'),
                                                country: countryName(headline.country, lang),
                                                share: headline.share,
                                            })}
                                        </p>
                                        <p className="text-xs text-muted-foreground">{t('BRAND_PORTAL.BENCHMARK.BASIS', { mine: formatNumber(headline.visualizations), market: formatNumber(headline.marketVisualizations), competitors: headline.competitors })}</p>
                                    </div>
                                ) : (
                                    <p className="py-6 text-center text-sm text-muted-foreground">{loading ? '' : t('BRAND_PORTAL.BENCHMARK.EMPTY')}</p>
                                )}
                            </SectionCard>
                        </div>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            <SectionCard
                                className="min-w-0"
                                title={t('BRAND_PORTAL.OVERVIEW.TOP_PRODUCTS')}
                                description={t('BRAND_PORTAL.OVERVIEW.TOP_PRODUCTS_TEXT')}
                                action={<Link to="/brand/demand?groupBy=product" className="text-xs text-muted-foreground hover:text-foreground">{t('BRAND_PORTAL.COMMON.SEE_ALL')}</Link>}
                                testId="brand-top-products"
                            >
                                <BarList
                                    emptyText={loading ? undefined : t('BRAND_PORTAL.OVERVIEW.TOP_EMPTY')}
                                    items={(o?.topProducts ?? []).map((r) => ({
                                        key: r.key,
                                        label: rowLabel(t, 'product', r, lang),
                                        value: r.visualizations,
                                        leading: <Thumb src={r.product?.thumbnailUrl} size="xs" />,
                                        secondary: <Change value={r.change.visualizations} />,
                                        href: r.product ? `/brand/products/${r.product.uuid}` : undefined,
                                    }))}
                                />
                            </SectionCard>
                            <SectionCard className="min-w-0" title={t('BRAND_PORTAL.OVERVIEW.TOP_REGIONS')} description={t('BRAND_PORTAL.OVERVIEW.TOP_REGIONS_TEXT')} testId="brand-top-regions"
                                action={<Link to="/brand/demand?groupBy=city" className="text-xs text-muted-foreground hover:text-foreground">{t('BRAND_PORTAL.OVERVIEW.BY_CITY')}</Link>}
                            >
                                <BarList
                                    emptyText={loading ? undefined : t('BRAND_PORTAL.OVERVIEW.TOP_EMPTY')}
                                    items={(o?.topRegions ?? []).map((r) => ({ key: r.key, label: rowLabel(t, 'country', r, lang), value: r.visualizations, secondary: <Change value={r.change.visualizations} /> }))}
                                />
                            </SectionCard>
                        </div>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            <SectionCard className="min-w-0" title={t('BRAND_PORTAL.OVERVIEW.RISING')} description={t('BRAND_PORTAL.OVERVIEW.RISING_TEXT')} testId="brand-rising">
                                {(o?.rising ?? []).length ? (
                                    <ul className="flex flex-col divide-y">
                                        {o.rising.map((r) => (
                                            <li key={r.key}>
                                                <Link to={`/brand/products/${r.key}`} className="flex items-center gap-3 rounded-md py-2.5 hover:bg-accent/40">
                                                    <Thumb src={r.product?.thumbnailUrl} size="sm" />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-sm">{r.label}</span>
                                                        <span className="block text-xs text-muted-foreground">{t('BRAND_PORTAL.OVERVIEW.RISING_ROW', { now: formatNumber(r.visualizations), before: formatNumber(r.previous?.visualizations ?? 0) })}</span>
                                                    </span>
                                                    {r.change.visualizations == null ? (
                                                        <span className="rounded-full border px-1.5 text-[11px] leading-5 font-medium text-success">{t('BRAND_PORTAL.COMMON.NEW')}</span>
                                                    ) : (
                                                        <Change value={r.change.visualizations} />
                                                    )}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="py-6 text-center text-sm text-muted-foreground">{loading ? '' : t('BRAND_PORTAL.OVERVIEW.RISING_EMPTY')}</p>
                                )}
                            </SectionCard>

                            <SectionCard
                                className="min-w-0"
                                title={t('BRAND_PORTAL.UNMET.TEASER_TITLE')}
                                description={t('BRAND_PORTAL.UNMET.TEASER_TEXT')}
                                action={<Link to="/brand/unmet" className="text-xs text-muted-foreground hover:text-foreground">{t('BRAND_PORTAL.COMMON.SEE_ALL')}</Link>}
                                testId="brand-unmet-teaser"
                            >
                                {teaser ? (
                                    <div className="flex items-start gap-3">
                                        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground">
                                            <Camera className="size-4" />
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-sm">
                                                {t('BRAND_PORTAL.UNMET.SENTENCE', {
                                                    count: teaser.searches,
                                                    look: [teaser.colorFamily && vocab(t, teaser.colorFamily).toLowerCase(), teaser.appearance && vocab(t, teaser.appearance).toLowerCase()].filter(Boolean).join(' ') || t('BRAND_PORTAL.COMMON.NOT_KNOWN').toLowerCase(),
                                                })}
                                            </p>
                                            {teaser.closest ? (
                                                <Link to={`/brand/products/${teaser.closest.uuid}`} className="mt-2 flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                                                    <Thumb src={teaser.closest.thumbnailUrl} size="xs" />
                                                    {t('BRAND_PORTAL.UNMET.CLOSEST', { name: teaser.closest.name, score: teaser.closest.similarity.toFixed(2) })}
                                                </Link>
                                            ) : (
                                                <p className="mt-2 text-xs text-muted-foreground">{t(data?.unmet?.closestAvailable ? 'BRAND_PORTAL.UNMET.NO_CLOSEST' : 'BRAND_PORTAL.UNMET.SURFACE_ID_OFF')}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="py-6 text-center text-sm text-muted-foreground">{loading ? '' : t('BRAND_PORTAL.UNMET.EMPTY_SHORT')}</p>
                                )}
                            </SectionCard>
                        </div>

                        <PrivacyFootnote />
                    </>
                )
            )}
        </BrandPage>
    );
}

export default BrandOverviewPage;
